import { useContext } from 'react';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import type { QuickChartData, SegmentationDashboardWidget, Segments, WidgetInstance, WidgetSourceMetadata } from '@trinity/chat';
import { createNamedContext } from '@trinity/chat-components/utils';

// API Context Types for shared library
export type SaveWidgetRequest = {
  projectId: number;
  title: string;
  data: {
    type: SegmentationDashboardWidget;
    props: {
      chart: QuickChartData;
      legendOpenDefault?: boolean;
      selectedThemeId?: number | null;
      colorOverrides?: Record<string, string> | null;
      labelOverrides?: Record<string, string> | null;
    };
  };
  sourceMetadata?: WidgetSourceMetadata;
};

export type AddWidgetToSectionRequest = {
  segmentId: number;
  sectionId: string;
  widgetId: number;
  insertPosition: number;
};

// Use existing types from the codebase but create a subset for dropdown usage
export type Segment = Pick<Segments.Segment, 'id' | 'name' | 'dashboard'>;

export type GetSegmentsResponse = {
  segments: Segment[];
};

// Context value interface
export interface ChartWidgetApiContextValue {
  // Save widget mutation
  useSaveWidget: () => UseMutationResult<WidgetInstance, Error, SaveWidgetRequest>;

  // Get segments query
  useGetSegments: (projectId: number, enabled?: boolean) => UseQueryResult<GetSegmentsResponse>;

  // Add widget to section mutation
  useAddWidgetToSection: (projectId: number) => UseMutationResult<unknown, Error, AddWidgetToSectionRequest>;
}

// Create the context using the utility function
export const ChartWidgetApiContext = createNamedContext<ChartWidgetApiContextValue | undefined>(
  undefined,
  'ChartWidget.Api.Context',
);

// Hook to use the context
export const useChartWidgetApi = (): ChartWidgetApiContextValue => {
  const context = useContext(ChartWidgetApiContext);
  if (!context) {
    throw new Error('useChartWidgetApi must be used within a ChartWidgetApiProvider');
  }
  return context;
};